<template>
  <Grid
    class="companies-grid"
    :pageable="controller.pageable.value"
    :data-items="companies"
    :skip="controller.skip.value"
    :take="controller.take.value"
    :total="totalCompanies"
    :columns="controller.columns.value"
    :loader="companiesLoading"
    :filter="filter"
    :column-menu="!companiesLoading"
    :resizable="true"
    ref="companiesGridRef"
    @columnresize="controller.onColumnResize($event)"
    @filterchange="controller.filterChangeHandler($event)"
    @sortchange="controller.sortChangeHandler($event)"
    @pagechange="controller.pageChangeHandler($event)"
    data-cy="companies-grid"
  >
    <template v-slot:columnMenu="{ props }">
      <ColumnMenu
        :column="props.column"
        :filterable="props.filterable"
        :filter="props.filter"
        :sortable="props.sortable"
        :sort="props.sort"
        :columns="controller.columns.value"
        :getItems="getPropValues"
        @sortchange="(e) => props.onSortchange(e)"
        @filterchange="(e) => props.onFilterchange(e)"
        @closemenu="(e) => props.onClosemenu(e)"
        @contentfocus="(e) => props.onContentfocus(e)"
        @columnssubmit="controller.onColumnsSubmit($event)"
      />
    </template>
    <template v-slot:PrintLocHeader="{ props: { header } }">
      <div>
        {{ header }}
      </div>
    </template>
    <template v-slot:PrintLocCell="{ props: { dataItem } }">
      <td class="k-table-td" data-cy="print-loc-td">
        <a
          v-if="locAvailable(dataItem)"
          @click="downloadLoc((dataItem as CompanyDisplay).VueId)"
        >
          <PdfFileIcon class="pdf-file-icon" />
        </a>
      </td>
    </template>
  </Grid>
</template>

<script setup lang="ts">
import { Grid } from "@progress/kendo-vue-grid";
import { onMounted, ref, watchEffect } from "vue";
import type { CompositeFilterDescriptor } from "@progress/kendo-data-query";
import type { CompanyDisplay } from "@/Domain/CompanyDisplay";
import PdfFileIcon from "@/icons/PdfFileIcon.vue";
import ColumnMenu from "@/shared/ui/inputs/ColumnMenu.vue";
import type {
  ItemChangedOptions,
  ItemSortChangedOptions,
} from "@/Domain/ItemChangedOptions";
import GridController from "@/shared/services/GridController";
import type { GridColumnPropsExtended } from "@/Domain/GridColumnPropsExtended";
import { useMainStore } from "@/stores/useMainStore";
import { printLocAsync } from "@/shared/helpers/printLocHelper";
import type { LocPrintRequest } from "@/Domain/LocPrintRequest";

const main = useMainStore();

const props = defineProps<{
  companies: CompanyDisplay[];
  companiesLoading: boolean;
  pageSize: number;
  skip: number;
  totalCompanies: number;
  filter: CompositeFilterDescriptor | undefined;
  getPropValues: (
    column: GridColumnPropsExtended
  ) => Promise<CompanyDisplay[]>;
}>();

const emit = defineEmits<{
  (e: "itemsChanged", options: ItemChangedOptions): void;
  (e: "sortChanged", options: ItemSortChangedOptions): void;
}>();

const companiesGridRef = ref<InstanceType<typeof Grid> | null>(null);

const columns = [
  {
    title: "Company",
    field: "Name",
    columnMenu: "columnMenu",
    minWidth: 120,
  },
  {
    title: "Company Category",
    field: "CompanyCategory",
    columnMenu: "columnMenu",
    minWidth: 150,
  },
  {
    title: "State",
    field: "_Address.state",
    columnMenu: "columnMenu",
    minWidth: 150,
  },
  {
    title: "Country",
    field: "_Address.country",
    columnMenu: "columnMenu",
    minWidth: 120,
  },
  {
    title: "Print LOC",
    headerClassName: "print-loc-header",
    cell: "PrintLocCell",
    sortable: false,
    filterable: false,
    width: "70px",
    minWidth: 70,
  },
] as GridColumnPropsExtended[];

const noLocCategories = ["Transport", "Tank Wash", "Storage"];

const locAvailable = (company: CompanyDisplay) => {
  return (
    company.IsOkToPrintCompanyLoc &&
    (!company.CompanyCategory ||
      !noLocCategories.some((c) => company.CompanyCategory.includes(c)))
  );
};

const controller = new GridController<CompanyDisplay>(
  "CompaniesGrid",
  columns,
  emit,
  props.pageSize
);

controller.initOptions();

const downloadLoc = (id: string) => {
  return printLocAsync({
    CompanyLogin: main.companyLogin,
    LocReportMyCompId: id,
    LocCalledFrom: "~/UkdCompanyList.aspx",
  } as LocPrintRequest);
};

watchEffect(() => {
  controller.skip.value = props.skip;
});

watchEffect(() => {
  controller.take.value = props.pageSize;
});

onMounted(() => {
  controller.setGridRef(companiesGridRef);
});
</script>

<style scoped lang="scss">
.companies-grid {
  min-height: 450px;
}

.k-grid {
  .print-loc-header {
    .k-grid-header-menu {
      display: none;
    }
  }

  td {
    a {
      color: #337ab7;
      cursor: pointer;
    }
  }
}

.pdf-file-icon {
  height: 36px;
}
</style>
